import { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import Srcset from "@/components/common/Srcset";
import ProductDes from "./ProductDescriptionComp";
import ShippingReturns from "./ShippingReturns";
import Notes from "./Notes";
import Accordion from "@/common/Accordion";
import ReactSidebar from "@/common/ReactSidebar";
import useMediaQuery from "@/helpers/hooks/useMediaQuery";

import styles from "../Sections/sections.module.scss";

import { lowerHeaderZIndex } from "@/redux/actions/headerAction";
import { generalConstants } from "@/utils/constants";
import { reportError } from "@/helpers/errorHelper";
import { getBrandDetails } from "@/helpers/pdpHelpers";

export default function Sections({ productDesc, isFinalSale, sizeGuideType, estimatedDeliveryDate, christmasMsg, tags, descriptionHtmlString, isExchangeOnly }) {
  const dispatch = useDispatch();

  // data for the shipping brand info 
  const [brandData, setBrandData] = useState({brand: "", logo: "", metaobjectChecked: false});
  const handleSidebar = (e,element) => {
    dispatch(lowerHeaderZIndex(e));
  };

  //fetch brand details only if api is not called before
  const handleBrandDetails = async () => {
    if(!brandData.metaobjectChecked && brandData.brand) {
      const brandDetails = await getBrandDetails(brandData.brand)
      const logo = Array.isArray(brandDetails) ? brandDetails?.find(f => f.key === "logo")?.value : null;
      if(logo) {
        setBrandData(brandObj => ({
          ...brandObj,
          logo: logo,
          metaobjectChecked: true
        }));
      } else {
        setBrandData(brandObj => ({
          ...brandObj,
          metaobjectChecked: true
        }));
      }
    }
  }

  const isMobile = useMediaQuery("(max-width: 767px)");


  const [scrollToBrand, setScrollToBrand] = useState(false);//flag to scroll to the shipping vendor info

  const need_assistance_border= typeof document !="undefined" && document.querySelector(".need_assistance_border")
  if(need_assistance_border) {
    if (isMobile) {
      need_assistance_border.style.borderBottom = "0.1rem solid #f2edea";
      need_assistance_border.style.paddingBottom = "7px"
    }
  }

  const [sizeAndFitListHtml, setSizeAndFitListHtml] = useState('');
  const [restOfContentHtml, setRestOfContentHtml] = useState('');
  const [tagValue, setTagValue] = useState('');

  const extractSizeAndFitList = (html) => {
    if (typeof window !== "undefined" && window.DOMParser) {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      
      // Find the <h5> element with the text "Size & Fit"
      const sizeFitHeader = Array.from(doc.querySelectorAll('h5')).find(h5 => h5.textContent.includes('Size & Fit'));
      
      // If found, get the next <ul> element
      if (sizeFitHeader) {
          sizeFitHeader.textContent = sizeFitHeader.textContent.replace('Fit', 'fit');
          const nextUl = sizeFitHeader.nextElementSibling;
          if (nextUl && nextUl.tagName === 'UL' && isMobile) {
              return nextUl.outerHTML; // Return the outer HTML of the <h5> and <ul>
          } else {
            return sizeFitHeader.outerHTML + nextUl.outerHTML;
          }
      }
    
    }
      
      return null; // Return null if not found
  };

  const extractRestOfContent = (html) => {
    if (typeof window !== "undefined" && window.DOMParser) {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      
      // Remove the Size & Fit section
      const sizeFitHeader = Array.from(doc.querySelectorAll('h5')).find(h5 => h5.textContent.includes('Size & Fit'));
      if (sizeFitHeader) {
          const nextUl = sizeFitHeader.nextElementSibling;
          sizeFitHeader.remove();
          if (nextUl && nextUl.tagName == 'UL') {
              nextUl.remove(); // Remove the <ul> after <h5>
          }
      }
      
      return doc.body.innerHTML; // Return the remaining content
    }
    return html; // Return the original HTML if DOMParser is not available
  };

  // Extract Size & Fit list

  useEffect(() => {
    if (productDesc) {
      try {
        //get tag lv1 value
        const categoryLvl1Tag = tags.find(tag => tag.startsWith("categories.lvl1:"));
        const categoryLvl1Value = categoryLvl1Tag ? categoryLvl1Tag.split(":")[1] : null;
        setTagValue(categoryLvl1Value);
        
        const parsedHtml = JSON.parse(productDesc);  
        const sizeAndFitHtml = extractSizeAndFitList(JSON.stringify(parsedHtml.descriptionHtml));
        const restOfContent = extractRestOfContent(JSON.stringify(parsedHtml.descriptionHtml));
        const vendorName = parsedHtml?.vendor || "";
    
        setSizeAndFitListHtml(sizeAndFitHtml);
        setRestOfContentHtml(restOfContent);
        // update brand name
        setBrandData(brandObj => ({
          ...brandObj,
          brand: vendorName, 
        }));
      } catch (error) {
        reportError("Error processing productDesc");
        reportError(error);
      }
    }
  }, [productDesc]);

  useEffect(() => {
    //fetch brand details from shopify metaobject
    if(tags?.includes("Dropship")) {
      handleBrandDetails()
    }
  },[brandData.brand])

  const accordionData = [
    {
      title: sizeAndFitListHtml ? "Product details" : "Product details & sizing",
      headerClass: "auto-details-mob",
      ga4Data: `PDP Product details click > ${tagValue}`,
      body: (
        <ProductDes
          productInfo={restOfContentHtml}
          showTitle={false}
          sizeGuideType={sizeGuideType}
          tagValue={tagValue}
          containsSizeDescrip={false}
        />
      ),
      icon: {
        src: "images/Icons/Plus/large.svg",
        alt: "plus",
        animationClass: styles.icon_transition,
      },
    },
    {
      title: "Shipping & returns",
      headerClass: "auto-shippingReturns-mob",
      subTitle: estimatedDeliveryDate,
      subTitle2: tags?.includes("Dropship") ? brandData.brand : null,
      hideSubTitle: true,
      body: <ShippingReturns showTitle={false} isFinalSale={isFinalSale} tags={tags} estimatedDeliveryDate={estimatedDeliveryDate} brand={brandData} isExchangeOnly={isExchangeOnly}/>,
      icon: {
        src: "images/Icons/Plus/large.svg",
        alt: "plus",
        animationClass: styles.icon_transition,
      },
    },
    // {
    //   title: "Notes",
    //   body: <Notes showTitle={false} />,
    //   icon: {
    //     src: "/images/Icons/Plus/large.svg",
    //     alt: "plus",
    //     animationClass: styles.icon_transition,
    //   },
    // },
  ];

  if (sizeAndFitListHtml) {
    accordionData.splice(1, 0, {
      title: "Size & fit",
      headerClass: "auto-sizeFit-mob",
      ga4Data: `PDP Size & fit click > ${tagValue}`,
      body: (
        <ProductDes
          productInfo={sizeAndFitListHtml}
          showTitle={false}
          sizeGuideType={sizeGuideType}
          tagValue={tagValue}
          containsSizeDescrip={true}
        />
      ),
      icon: {
        src: "images/Icons/Plus/large.svg",
        alt: "plus",
        animationClass: styles.icon_transition,
      }
    })
  }

  const sidebarData = [
    {
      title: sizeAndFitListHtml ? "Product details" : "Product details & sizing",
      headerClass: "auto-details",
      ga4Data: `PDP Product details click > ${tagValue}`,
      body: (
        <ProductDes
          productInfo={restOfContentHtml}
          showTitle={true}
          sizeGuideType={sizeGuideType}
          containsSizeDescrip={false}
          tagValue={tagValue}
        />
      ),
      icon: {
        src: "images/Icons/Arrow/Right/large.svg",
        alt: "right-arrow",
      },
    },
    {
      title: "Shipping & returns",
      headerClass: "auto-shippingReturns",
      subTitle: estimatedDeliveryDate,
      subTitle2: tags?.includes("Dropship") ? brandData.brand : null,
      body: <ShippingReturns showTitle={true} isFinalSale={isFinalSale} christmasMsg={christmasMsg} tags={tags} estimatedDeliveryDate={estimatedDeliveryDate} brand={brandData} isExchangeOnly={isExchangeOnly}/>,
      icon: {
        src: "images/Icons/Arrow/Right/large.svg",
        alt: "right-arrow",
      },
    },
    // {
    //   title: "Notes",
    //   body: <Notes showTitle={true} />,
    //   icon: {
    //     src: "/images/Icons/Arrow/Right/large.svg",
    //     alt: "right-arrow",
    //   },
    // },
  ];

  if (sizeAndFitListHtml) {
    sidebarData.splice(1, 0, {
      title: "Size & fit",
      headerClass: "auto-sizeFit-mob",
      ga4Data: `PDP Size & fit click > ${tagValue}`,
      body: (
        <ProductDes
          productInfo={sizeAndFitListHtml}
          showTitle={true}
          sizeGuideType={sizeGuideType}
          containsSizeDescrip={true}
          tagValue={tagValue}
        />
      ),
      icon: {
        src: "images/Icons/Arrow/Right/large.svg",
        alt: "right-arrow",
      }
    });
  }

  // to open the drawer and scroll to the info on clicking of the vendor name
  useEffect(() => {
    if (scrollToBrand) {
      if(typeof document !== "undefined") {
        const brandElement = document.querySelector(".shipping_vendor_sub_block");
        if (brandElement) {
          brandElement.scrollIntoView({ behavior: "smooth", block: "start" });
          setScrollToBrand(false);
        }
      }
    }
  },[scrollToBrand]);

  return (
    <>
      {/* Desktop View */}
      {!isMobile && (
         <div className={`d-none d-md-block pdp-sections-block ${styles.section_wrapper}`}>
          <ReactSidebar
            key={0}
            mainContent={
              <div
                tabIndex={0} className={`small-xs small-sm large-md ${styles.section_header} ${sidebarData[0].headerClass && sidebarData[0].headerClass}`}
              >
                <span className="pdp-ga4-click-trigger" data-pdpGa4={sidebarData[0]?.ga4Data}>{sidebarData[0]?.title}</span>
                <div className={`${styles["right-arrow"]}`}>
                  <Srcset
                    className="pdp-ga4-click-trigger"
                    height={15}
                    width={15}
                    alt={sidebarData[0]?.icon?.alt}
                    requirePath={true}
                    svgFile={true}
                    src={sidebarData[0]?.icon?.src}
                    data-pdpGa4={sidebarData[0]?.ga4Data}
                  />
                </div>
                <span className={`x-small-md x-small-sm ${styles.pdp_date_text} italic`}>{sidebarData[0].subTitle && sidebarData[0].subTitle}</span>
                <div className="row">
                  <div
                    id="descPDP"
                    className={`x-small-xs small-sm normal-md ${styles.short_desc} ${!sizeAndFitListHtml ? styles.short_desc_sizing : ""}`}
                  >
                    <div
                      dangerouslySetInnerHTML={{ __html: descriptionHtmlString }}>
                    </div>
                  </div>
                </div>
              </div>
            }
            handleSidebar={handleSidebar}
            sidebarContent={sidebarData[0]?.body}
            rootClassName={`${styles.root_class} ${styles.product_details_height}`}
            pullRight={true}
            styles={{
              sidebar: {
                position: "fixed",
                overflow: "auto",
              },
            }}
          />
          {sidebarData.slice(1).map((ele, index) => (
              <ReactSidebar
                key={1}
                mainContent={
                  <div
                    tabIndex={0} className={`small-xs small-sm large-md ${styles.section_header} ${ele.headerClass && ele.headerClass}`}
                  >
                    <span className="pdp-ga4-click-trigger" data-pdpGa4={ele?.ga4Data}>{ele?.title}</span>
                    <div className={`${styles["right-arrow"]}`}>
                      <Srcset
                        className="pdp-ga4-click-trigger"
                        height={15}
                        width={15}
                        alt={ele?.icon?.alt}
                        requirePath={true}
                        svgFile={true}
                        src={ele?.icon?.src}
                        data-pdpGa4={ele?.ga4Data}
                      />
                    </div>
                    <span className={`x-small-md x-small-sm ${styles.pdp_date_text} italic`}>{ele.subTitle && ele.subTitle}</span>
                    {ele.subTitle2 ? <span className={`x-small-md x-small-sm italic ${styles.pdp_date_text}`}>Shipped By&nbsp;
                      <a onClick={(e) => {
                        e.preventDefault();
                        setScrollToBrand(true);
                    }}
                    className="ga-pdp-shipping-vendor">{ele.subTitle2}</a></span> : null}
                  </div>
                }
                handleSidebar={handleSidebar}
                sidebarContent={ele?.body}
                rootClassName={`${styles.root_class} ${ele.title === "Shipping & returns" && tags?.includes("Dropship") ? styles.shipping_height : ""}`}
                pullRight={true}
                styles={{
                  sidebar: {
                    position: "fixed",
                    overflow: "auto",
                  },
                }}
              />
              ))}

          {/* Speak to a stylist (static) */}
          <div
            tabIndex={0} className={`small-xs small-sm large-md ${styles.section_header} ${estimatedDeliveryDate ? styles.section_header_stylist : ""} ${tags?.includes("Dropship") ? styles.stylist_header : ""}`}
            onClick={() => { window.location = `mailto:${generalConstants.help_email}` }}
          >
            <span>Need assistance?</span>
            <div className={`${styles["email"]}`}>
              <a tabIndex={-1} href={`mailto:${generalConstants.help_email}`}>
                <Srcset
                  height={15}
                  width={15}
                  alt="Email"
                  requirePath={true}
                  svgFile={true}
                  src="images/Icons/Email.svg"
                />
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Mobile View */}
      {isMobile && (
        <div className="d-none d-sm-block">
          <Accordion data={accordionData} defaultOpen={true}/>

          {/* Speak to a stylist (static) */}
          <div className={`typography-wrap ${estimatedDeliveryDate && "typography-pdp-date"}`}>
            <div className="custom-accordion">
              <div className="accordion-item need_assistance_border">
                <div className="accordion-head" onClick={() => { window.location = `mailto:${generalConstants.help_email}` }}>
                  <span className="name"> Need assistance?</span>
                  <span className="right">
                    <a href={`mailto:${generalConstants.help_email}`}>
                      <Srcset
                        height={14}
                        width={14}
                        requirePath={true}
                        svgFile={true}
                        src="images/Icons/Email.svg"
                        alt="email"
                      />
                    </a>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
